const { useState } = React;
import { bgUrl, deleteCueImage, normalizePhotoName, uploadCueImage } from "../data.jsx";
import { useT } from "../i18n.jsx";
import { usePhotoLibrary } from "../photoLibrary.jsx";
import { Check, ImageIcon, Loader2, Lock, Pencil, Plus, RefreshIcon, SearchIcon, T, Trash2 } from "../theme.jsx";

// ========================================================== PHOTO LIBRARY =====
function PhotoLibraryScreen({ perms }) {
  const { t } = useT();
  const { list, loading, addFreshToLibrary, removeFromLibrary, renamePhoto, replacePhoto } = usePhotoLibrary();
  const [replacingId, setReplacingId] = useState(null);
  const [query, setQuery] = useState("");
  const [adding, setAdding] = useState(false);
  const [pendingAssetId, setPendingAssetId] = useState("");
  const [pendingName, setPendingName] = useState("");
  const [uploadBusy, setUploadBusy] = useState(false);
  const [saveBusy, setSaveBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [renameBusy, setRenameBusy] = useState(false);

  const filtered = list.filter((p) => normalizePhotoName(p.name).includes(normalizePhotoName(query)));

  const startAdding = () => { setAdding(true); setPendingAssetId(""); setPendingName(""); setError(""); };
  const cancelAdding = () => {
    if (pendingAssetId) deleteCueImage(pendingAssetId); // discard the just-uploaded, never-saved asset
    setAdding(false); setPendingAssetId(""); setPendingName("");
  };
  const handleFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploadBusy(true); setError("");
    try { const id = await uploadCueImage(f); setPendingAssetId(id); }
    catch (err) { setError(t("uploadFailed")); }
    setUploadBusy(false);
    e.target.value = "";
  };
  const saveEntry = async () => {
    if (!pendingAssetId) return;
    setSaveBusy(true); setError("");
    try { await addFreshToLibrary(pendingName, pendingAssetId); setAdding(false); setPendingAssetId(""); setPendingName(""); }
    catch (e) {
      setError(e && e.message === "duplicate-name" ? t("photoNameDupError") : e && e.message === "empty-name" ? t("photoNameEmptyError") : t("uploadFailed"));
    }
    setSaveBusy(false);
  };
  const doDelete = async (id) => {
    const ok = await removeFromLibrary(id);
    if (!ok) setError(t("deleteFailed"));
    setConfirmDeleteId(null);
  };
  const handleReplace = async (id, e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    setReplacingId(id); setError("");
    try { await replacePhoto(id, await uploadCueImage(f)); }
    catch (err) { setError(t("replacePhotoFailed")); }
    setReplacingId(null);
  };
  const startRename = (p) => { setRenamingId(p.id); setRenameValue(p.name); setError(""); };
  const cancelRename = () => { setRenamingId(null); setRenameValue(""); };
  const saveRename = async (id) => {
    setRenameBusy(true); setError("");
    try { await renamePhoto(id, renameValue); setRenamingId(null); setRenameValue(""); }
    catch (e) {
      setError(e && e.message === "duplicate-name" ? t("photoNameDupError") : e && e.message === "empty-name" ? t("photoNameEmptyError") : t("renameFailed"));
    }
    setRenameBusy(false);
  };

  return (
    <div style={{ padding: 16, textAlign: "center" }}>
      <div style={{ maxWidth: 460, margin: "0 auto 16px" }}>
        <div style={{ fontSize: 13, color: T.subtext }}>{t("photoLibraryDesc")}</div>
      </div>

      {error && <div style={{ background: T.dangerBg, color: T.danger, fontSize: 12, padding: "8px 12px", borderRadius: 8, marginBottom: 12, maxWidth: 420, marginLeft: "auto", marginRight: "auto" }}>{error}</div>}

      {perms.canMaster && (
        adding ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, marginBottom: 16, background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 16, maxWidth: 280, marginLeft: "auto", marginRight: "auto" }}>
            {pendingAssetId ? (
              <img src={bgUrl(pendingAssetId)} alt="" style={{ width: 96, height: 96, objectFit: "cover", borderRadius: 10, border: `1px solid ${T.border}` }} />
            ) : (
              <label className="rb-btn" style={{ width: 96, height: 96, borderRadius: 10, border: `1.5px dashed ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center", cursor: uploadBusy ? "default" : "pointer", color: T.subtext }}>
                {uploadBusy ? <Loader2 size={18} style={{ animation: "rb-spin 1s linear infinite" }} /> : <ImageIcon size={20} />}
                <input type="file" accept="image/*" disabled={uploadBusy} style={{ display: "none" }} onChange={handleFile} />
              </label>
            )}
            <input aria-label={t("photoNamePh")} className="rb-input" autoFocus value={pendingName} onChange={(e) => setPendingName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") saveEntry(); if (e.key === "Escape") cancelAdding(); }}
              placeholder={t("photoNamePh")} style={{ width: "100%", boxSizing: "border-box", fontSize: 13, border: `1px solid ${T.border}`, borderRadius: 8, padding: "7px 10px", background: T.panel2, color: T.text, textAlign: "center" }} />
            <div style={{ display: "flex", gap: 6 }}>
              <button className="rb-btn" onClick={saveEntry} disabled={!pendingAssetId || saveBusy}
                style={{ background: T.accent, color: "#161513", border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, fontWeight: 700, cursor: (!pendingAssetId || saveBusy) ? "default" : "pointer", opacity: (!pendingAssetId || saveBusy) ? 0.5 : 1, display: "flex", alignItems: "center", gap: 5 }}>
                <Lock size={12} /> {saveBusy ? t("uploading") : t("saveToLibraryConfirm")}
              </button>
              <button className="rb-btn" onClick={cancelAdding} style={{ background: T.panel2, border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 12.5, cursor: "pointer", color: T.text }}>{t("cancel")}</button>
            </div>
          </div>
        ) : (
          <button className="rb-btn" onClick={startAdding} style={{ display: "flex", alignItems: "center", gap: 6, background: T.accent, color: "#161513", border: "none", borderRadius: 10, padding: "10px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer", marginBottom: 16, marginLeft: "auto", marginRight: "auto" }}>
            <Plus size={14} /> {t("addPhotoBtn")}
          </button>
        )
      )}

      {list.length > 0 && (
        <div style={{ maxWidth: 320, margin: "0 auto 14px", display: "flex", alignItems: "center", gap: 6, background: T.panel2, border: `1px solid ${T.border}`, borderRadius: 8, padding: "6px 10px" }}>
          <SearchIcon size={13} color={T.subtext} />
          <input aria-label={t("searchPhotosPh")} className="rb-input" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPhotosPh")} style={{ flex: 1, minWidth: 0, fontSize: 12.5, border: "none", background: "transparent", color: T.text, textAlign: "left" }} />
        </div>
      )}

      {loading ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("loading")}</div> :
        list.length === 0 ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("photoLibraryEmpty")}</div> :
        filtered.length === 0 ? <div style={{ color: T.subtext, fontSize: 13 }}>{t("noPhotosFoundSearch")}</div> :
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10, maxWidth: 720, margin: "0 auto" }}>
          {filtered.map((p) => (
            <div key={p.id} style={{ background: T.panel, border: `1px solid ${T.border}`, borderRadius: 14, padding: 10, position: "relative" }}>
              <div style={{ width: "100%", aspectRatio: "1 / 1", borderRadius: 10, background: T.panel2, backgroundImage: `url(${bgUrl(p.bg)})`, backgroundSize: "cover", backgroundPosition: "center", marginBottom: 8, position: "relative" }}>
                <span style={{ position: "absolute", top: 5, left: 5, background: "rgba(0,0,0,0.55)", borderRadius: 5, padding: "2px 5px", display: "flex", alignItems: "center" }}>
                  <Lock size={9} color="#fff" />
                </span>
                {replacingId === p.id && (
                  <div style={{ position: "absolute", inset: 0, borderRadius: 10, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Loader2 size={20} color="#fff" style={{ animation: "rb-spin 1s linear infinite" }} />
                  </div>
                )}
                {perms.canMaster && replacingId !== p.id && (
                  <label className="rb-btn" title={t("replacePhotoTitle")} data-replace-photo={p.name}
                    style={{ position: "absolute", bottom: 5, right: 5, background: "rgba(0,0,0,0.62)", color: "#fff", borderRadius: 6, padding: "3px 7px", fontSize: 10.5, fontWeight: 700, display: "flex", alignItems: "center", gap: 4, cursor: replacingId ? "default" : "pointer" }}>
                    <RefreshIcon size={10} /> {t("replacePhotoBtn")}
                    <input aria-label={`${t("replacePhotoTitle")}: ${p.name}`} type="file" accept="image/*" disabled={!!replacingId} style={{ display: "none" }} onChange={(e) => handleReplace(p.id, e)} />
                  </label>
                )}
              </div>
              {renamingId === p.id ? (
                <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                  <input aria-label={t("editToggle")} className="rb-input" autoFocus value={renameValue} onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") saveRename(p.id); if (e.key === "Escape") cancelRename(); }}
                    disabled={renameBusy}
                    style={{ flex: 1, minWidth: 0, fontSize: 12, border: `1px solid ${T.border}`, borderRadius: 6, padding: "4px 6px", background: T.panel2, color: T.text, textAlign: "left" }} />
                  <button aria-label={t("editToggle")} className="rb-btn" onClick={() => saveRename(p.id)} disabled={renameBusy} title={t("editToggle")}
                    style={{ background: "none", border: "none", color: T.accentInk, cursor: renameBusy ? "default" : "pointer", padding: 2, flexShrink: 0 }}><Check size={13} /></button>
                  <button aria-label={t("cancel")} className="rb-btn" onClick={cancelRename} disabled={renameBusy}
                    style={{ background: "none", border: "none", color: T.subtext, cursor: renameBusy ? "default" : "pointer", padding: 2, flexShrink: 0 }}>✕</button>
                </div>
              ) : (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }} title={p.name}>{p.name}</div>
                  {perms.canMaster && (
                    <button aria-label={t("renamePhotoTitle")} className="rb-btn" onClick={() => startRename(p)} title={t("renamePhotoTitle")}
                      style={{ background: "none", border: "none", color: T.subtext, cursor: "pointer", padding: 2, flexShrink: 0, display: "flex" }}><Pencil size={11} /></button>
                  )}
                </div>
              )}
              {perms.canMaster && renamingId !== p.id && (
                confirmDeleteId === p.id ? (
                  <div style={{ display: "flex", gap: 4, marginTop: 6, justifyContent: "center" }}>
                    <button className="rb-btn" onClick={() => doDelete(p.id)} style={{ background: T.danger, color: "#fff", border: "none", borderRadius: 6, fontSize: 11, padding: "3px 6px", cursor: "pointer" }}>{t("deleteBtn")}</button>
                    <button aria-label={t("cancel")} className="rb-btn" onClick={() => setConfirmDeleteId(null)} style={{ background: T.panel2, color: T.text, border: "none", borderRadius: 6, fontSize: 11, padding: "3px 6px", cursor: "pointer" }}>✕</button>
                  </div>
                ) : (
                  <button aria-label={t("deleteBtn")} className="rb-btn" onClick={() => setConfirmDeleteId(p.id)} title={t("deleteBtn")} style={{ position: "absolute", top: 10, right: 10, background: "none", border: "none", color: T.danger, cursor: "pointer" }}><Trash2 size={13} /></button>
                )
              )}
            </div>
          ))}
        </div>}
    </div>
  );
}

export { PhotoLibraryScreen };
